import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { addDaysIso, todayIso } from '../../utils/formatters.js';
import { EmployeeCreateModal } from './EmployeeCreateModal.jsx';

const EMPTY = {
  employeeId: '', leaveTypeId: '',
  startDate: todayIso(), endDate: addDaysIso(1),
  reason: '',
};

export function LeaveRequestModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

  const employees = useQuery({
    queryKey: ['hr', 'employees', 'active'],
    queryFn: () => hrApi.listEmployees({ status: 'ACTIVE' }).then((r) => r.items || []),
    enabled: open,
  });
  const leaveTypes = useQuery({
    queryKey: ['hr', 'leave-types'],
    queryFn: () => hrApi.listLeaveTypes().then((r) => r.items || []),
    enabled: open,
  });

  const submit = useMutation({
    mutationFn: (body) => hrApi.submitLeave(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'leave-requests'] });
      setForm(EMPTY);
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const handle = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  function send(e) {
    e.preventDefault();
    setError(null);
    submit.mutate({
      employeeId: form.employeeId,
      leaveTypeId: form.leaveTypeId,
      startDate: form.startDate,
      endDate: form.endDate,
      reason: form.reason.trim() || undefined,
    });
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t('hr.leave.modal.title')}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="primary" onClick={send} disabled={submit.isPending}>
              {submit.isPending ? `${t('common.save')}…` : t('common.create')}
            </Button>
          </>
        }
      >
        <form onSubmit={send} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <label className="field">
            <span className="field-label">{t('hr.leave.modal.employee')}</span>
            <div className="inline-create">
              <select className="select" value={form.employeeId} onChange={handle('employeeId')} required>
                <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
                {(employees.data || []).map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.employeeNumber} - {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
              <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setEmployeeModalOpen(true)}>
                + {t('common.new')}
              </Button>
            </div>
          </label>
          <label className="field">
            <span className="field-label">{t('hr.leave.modal.leaveType')}</span>
            <select className="select" value={form.leaveTypeId} onChange={handle('leaveTypeId')} required>
              <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
              {(leaveTypes.data || []).map((lt) => (
                <option key={lt.id} value={lt.id}>{lt.code} - {lt.name}</option>
              ))}
            </select>
          </label>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('hr.leave.modal.startDate')}</span>
              <input className="input" type="date" value={form.startDate} onChange={handle('startDate')} required />
            </label>
            <label className="field">
              <span className="field-label">{t('hr.leave.modal.endDate')}</span>
              <input className="input" type="date" value={form.endDate} onChange={handle('endDate')} required />
            </label>
          </div>
          <label className="field">
            <span className="field-label">{t('hr.leave.modal.reason')}</span>
            <textarea className="textarea" rows={3} value={form.reason} onChange={handle('reason')} />
          </label>
          {error && <div className="alert danger">{error}</div>}
        </form>
      </Modal>
      <EmployeeCreateModal
        open={employeeModalOpen}
        onClose={() => setEmployeeModalOpen(false)}
        onCreated={(created) => {
          queryClient.invalidateQueries({ queryKey: ['hr', 'employees', 'active'] });
          if (created?.id) {
            setForm((p) => ({ ...p, employeeId: created.id }));
          }
        }}
      />
    </>
  );
}
